import { Link, useRouterState } from '@tanstack/react-router'
import { LayoutDashboard, SlidersHorizontal, PieChart, TrendingUp, PanelLeftClose, PanelLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

const navigation = [
  { name: 'Scenarios', to: '/scenarios', icon: LayoutDashboard },
  { name: 'Portfolio Adjustments', to: '/adjustments', icon: SlidersHorizontal },
  { name: 'Asset Classification', to: '/assets', icon: PieChart },
  { name: 'Results Dashboard', to: '/results', icon: TrendingUp },
]

interface SidebarProps {
  collapsed?: boolean
  onToggle?: () => void
}

export function Sidebar({ collapsed = false, onToggle }: SidebarProps) {
  const routerState = useRouterState()
  const currentPath = routerState.location.pathname

  return (
    <aside className={cn(
      "fixed left-0 top-14 z-30 flex h-[calc(100vh-3.5rem)] flex-col border-r bg-slate-900 text-white transition-all duration-300",
      collapsed ? "w-16" : "w-64",
      "hidden md:flex"
    )}>
      {/* Collapse toggle button */}
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          "absolute -right-3 top-6 z-50 h-6 w-6 rounded-full border bg-background text-foreground shadow-md",
          "hidden md:flex hover:bg-accent hover:shadow-lg"
        )}
        onClick={onToggle}
      >
        {collapsed ? (
          <PanelLeft className="h-4 w-4" />
        ) : (
          <PanelLeftClose className="h-4 w-4" />
        )}
        <span className="sr-only">Toggle sidebar</span>
      </Button>

      <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-4">
        <nav className="flex flex-col gap-1">
          {navigation.map((item) => {
            const Icon = item.icon
            const isActive = currentPath === item.to || currentPath.startsWith(`${item.to}/`)

            return (
              <Link
                key={item.name}
                to={item.to}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-all',
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-slate-300 hover:bg-slate-800/50 hover:text-white',
                  collapsed && "justify-center px-2"
                )}
                title={collapsed ? item.name : undefined}
              >
                <Icon className={cn("h-5 w-5 flex-shrink-0", collapsed && "mr-0")} />
                {!collapsed && <span className="truncate">{item.name}</span>}
              </Link>
            )
          })}
        </nav>
      </div>
      
      {!collapsed && (
        <div className="border-t border-slate-700 p-4">
          <div className="flex items-center gap-3 text-xs text-slate-400">
            <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
            <span>System Operational</span>
          </div>
        </div>
      )}
    </aside>
  )
}
